import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import {
  FlexColumn,
  RadioGroup,
  SearchField,
} from '@elementary/common-ui/components';
import MergeIncidentCard, {
  type MergeIncidentCardItem,
} from './MergeIncidentCard';

const SectionLabel = styled(Typography)`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.common.gray4};
  text-transform: uppercase;
`;

const HelperText = styled(Typography)`
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const SelectorRoot = styled(FlexColumn)`
  min-width: 0;
  min-height: 0;
  flex: 1 1 0;
`;

const Header = styled(FlexColumn)`
  gap: 12px;
  padding: 24px 32px 16px;
  flex: 0 0 auto;
`;

const CardsDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.palette.common.gray8};
  flex: 0 0 auto;
`;

const CardsGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 12px;
  min-width: 0;
  min-height: 0;
  width: 100%;
  flex: 1;
  overflow-y: auto;
  padding: 16px 32px 24px;
`;

type Props = {
  incidents: MergeIncidentCardItem[];
  primaryId: string;
  onPrimaryChange: (id: string) => void;
};

const MergeIncidentSelector = ({
  incidents,
  primaryId,
  onPrimaryChange,
}: Props) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return incidents;
    const q = search.trim().toLowerCase();
    return incidents.filter((i) => i.title.toLowerCase().includes(q));
  }, [incidents, search]);

  return (
    <SelectorRoot>
      <Header>
        <FlexColumn sx={{ gap: 0.5 }}>
          <SectionLabel>SELECT PRIMARY INCIDENT</SectionLabel>
          <HelperText>
            The primary incident remains after merging. Others are merged into
            it.
          </HelperText>
        </FlexColumn>
        <SearchField
          placeholder="Filter incidents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: '100%', maxWidth: 360 }}
        />
      </Header>
      <CardsDivider />
      <CardsGroup value={primaryId} onChange={(_, v) => onPrimaryChange(v)}>
        {filtered.map((incident) => (
          <MergeIncidentCard
            key={incident.id}
            incident={incident}
            selected={incident.id === primaryId}
            onSelect={onPrimaryChange}
          />
        ))}
      </CardsGroup>
    </SelectorRoot>
  );
};

export default MergeIncidentSelector;
